import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, UploadCloud, FileText, ListChecks, Inbox, FolderOpen, LogOut, Menu, UserPlus, Building2,
} from "lucide-react";
import { LOGO_URL, BRAND } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import Dashboard from "@/components/app/Dashboard";
import ConsumersCRM from "@/components/app/ConsumersCRM";
import ReportUpload from "@/components/app/ReportUpload";
import LetterStudio from "@/components/app/LetterStudio";
import DisputeTracker from "@/components/app/DisputeTracker";
import RequestsInbox from "@/components/app/RequestsInbox";
import DocumentLibrary from "@/components/app/DocumentLibrary";
import Team from "@/components/app/Team";
import Owners from "@/components/app/Owners";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "crm", label: "Consumer CRM", icon: Users },
  { key: "upload", label: "Report Upload", icon: UploadCloud },
  { key: "letters", label: "Letter Studio", icon: FileText },
  { key: "tracking", label: "Dispute Tracking", icon: ListChecks },
  { key: "documents", label: "Documents", icon: FolderOpen },
  { key: "requests", label: "Client Requests", icon: Inbox },
  { key: "team", label: "Team", icon: UserPlus },
  { key: "agencies", label: "Agencies", icon: Building2, adminOnly: true },
];

export default function Workspace() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-72 bg-[#F6EEEE] border-r border-neutral-200 flex flex-col transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-20 flex items-center gap-3 px-6 border-b border-neutral-200">
          <span className="bg-white rounded-lg p-1.5"><img src={LOGO_URL} alt={BRAND} className="h-9 w-auto object-contain" /></span>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-sm">{BRAND}</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#C1121F]">CRM Workspace</div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1.5">
          {TABS.filter((t) => !t.adminOnly || user?.role === "admin").map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => { setTab(t.key); setMobileOpen(false); }}
                data-testid={`nav-tab-${t.key}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  active ? "bg-[#C1121F] text-white" : "text-neutral-600 hover:bg-neutral-100 hover:text-[#C1121F]"
                }`}>
                <t.icon className="h-4.5 w-4.5" /> {t.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-neutral-200">
          <div className="px-2 mb-3">
            <div className="text-sm font-medium text-neutral-900 truncate">{user?.name}</div>
            <div className="text-xs text-neutral-400 truncate">{user?.email}</div>
          </div>
          <Button onClick={doLogout} variant="outline"
            className="w-full border-neutral-200 bg-neutral-50 hover:bg-neutral-200 text-neutral-800" data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </Button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="h-20 border-b border-neutral-200 glass sticky top-0 z-20 flex items-center justify-between px-4 sm:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)}><Menu className="h-6 w-6" /></button>
            <h1 className="font-display text-xl font-bold">{TABS.find((t) => t.key === tab)?.label}</h1>
          </div>
          <button onClick={() => navigate("/")} className="text-sm text-neutral-500 hover:text-[#C1121F] transition-colors">View site</button>
        </header>
        <div className="p-4 sm:p-8 max-w-7xl">
          {tab === "dashboard" && <Dashboard goTo={setTab} />}
          {tab === "crm" && <ConsumersCRM />}
          {tab === "upload" && <ReportUpload />}
          {tab === "letters" && <LetterStudio />}
          {tab === "tracking" && <DisputeTracker />}
          {tab === "documents" && <DocumentLibrary />}
          {tab === "requests" && <RequestsInbox />}
          {tab === "team" && <Team />}
          {tab === "agencies" && <Owners />}
        </div>
      </main>
    </div>
  );
}
